// TODO: Write tests for this file

import { useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { DOCS_PATHNAME_PART } from "./constants";

export type UsageBlockWithDescriptionComponent = {
  Description: React.ComponentType;
  code: string;
  codeDependencies: Record<string, any>;
};

export type UsageBlockWithDescriptionJsx = {
  description: JSX.Element | null;
  code: string;
  codeDependencies: Record<string, any>;
};

export type FederatedModulesDocsPageUsageBlock =
  | UsageBlockWithDescriptionComponent
  | UsageBlockWithDescriptionJsx;

export type FederatedModulesDocsConfig = {
  usageBlocks: FederatedModulesDocsPageUsageBlock[];
};

export type FederatedModulesDocsAppEntry = {
  name: string;
  route: string;
  Component: React.ComponentType;
};

export type DynamicDocsImports = {
  importCacheOfExposesDocs: any;
  importCacheOfExposesDocgens: any;
  importCacheOfPackageExposesTypes: any;
};

export type DynamicDocs = {
  docs: FederatedModulesDocsConfig;
  name: string;
  route: string;
  docgenResult: any;
  propsFile: string;
};

export const getDocsPathnameOfComponent = (componentName: string): string =>
  `/${componentName.toLowerCase()}`;

const getDynamicDocs = ({
  imports,
}: {
  imports: DynamicDocsImports;
}): DynamicDocs[] => {
  const {
    importCacheOfExposesDocs,
    importCacheOfExposesDocgens,
    importCacheOfPackageExposesTypes,
  } = imports;

  return Object.keys(importCacheOfExposesDocs)
    .map((key): DynamicDocs | undefined => {
      const docsModule = importCacheOfExposesDocs[key];

      // Find the last part of the path that begins with a capital letter. We will assume this is the name.
      const possibleNames = key
        .split("/")
        .reverse()
        .filter((x) => x.match(/^[A-Z]/));

      if (!possibleNames.length) {
        console.error(
          `Unable to find determine a valid name from "${key}". Skipping.`,
        );
        return undefined;
      }
      const name = possibleNames[0];

      const docs = docsModule.default;

      if (!docs.usageBlocks) {
        console.error(`Unable to find docs usageBlocks for ${name}. Skipping.`);
        return undefined;
      }

      const docgenResultKey = Object.keys(importCacheOfExposesDocgens).find(
        (key) => key.includes(`/${name}/`),
      );
      if (!docgenResultKey) {
        console.error(`Unable to find docgenResult for ${name}. Skipping.`);
        return undefined;
      }
      const docgenResult = importCacheOfExposesDocgens[docgenResultKey];

      const propsFileKey = Object.keys(importCacheOfPackageExposesTypes).find(
        (key) => key.includes(`/${name}/`),
      );
      if (!propsFileKey) {
        console.error(`Unable to find propsFile for ${name}. Skipping.`);
        return undefined;
      }
      const propsFile: string =
        importCacheOfPackageExposesTypes[propsFileKey].default;

      return {
        docs,
        name,
        route: getDocsPathnameOfComponent(name),
        docgenResult,
        propsFile,
      };
    })
    .filter((x) => !!x)
    .map((x) => x as NonNullable<typeof x>);
};

export const useDynamicDocs = ({
  imports,
}: {
  imports: DynamicDocsImports;
}): DynamicDocs[] => {
  return useMemo(() => getDynamicDocs({ imports }), [imports]);
};

// Custom type guards
// Source: https://rangle.io/blog/how-to-use-typescript-type-guards/

export const isUsageBlockWithDescriptionComponent = (
  usageBlock: FederatedModulesDocsPageUsageBlock,
): usageBlock is UsageBlockWithDescriptionComponent =>
  (usageBlock as UsageBlockWithDescriptionComponent).Description !== undefined;

export const isUsageBlockWithDescriptionJsx = (
  usageBlock: FederatedModulesDocsPageUsageBlock,
): usageBlock is UsageBlockWithDescriptionJsx =>
  (usageBlock as UsageBlockWithDescriptionJsx).description !== undefined;

// Scroll to top on location.pathname change.
// Source: https://reactrouter.com/web/guides/scroll-restoration
export const useScrollToTopOnPathnameChange = (elementId: string): void => {
  const { pathname } = useLocation();

  useEffect(() => {
    const docsContentElement = document.getElementById(elementId);
    if (docsContentElement) {
      docsContentElement.scrollTo(0, 0);
    }
  }, [pathname, elementId]);
};

// Logic to get proper formatted routes within the docs

export type PathnamePartsOverrides = {
  docsApp?: string | undefined | null;
  docsPage?: string | undefined | null;
};

/**
 * Gets the proper formatted route in the docs ecosystem.
 *
 * Each override in pathnamePartsOverrides can be:
 *
 *   - undefined (or empty string): In which case,
 *     the corresponding part of the route is left as is.
 *
 *   - null: In which case,
 *     the corresponding part of the route is removed
 *
 *   - string: In which case,
 *     the corresponding part of the route is replaced with the given value.
 *
 * The overrides in pathnamePartsOverrides correspond to the following:
 *   /my-components/federated-modules-docs/my-components/mycomponentv1
 *   /<appPathnamePart>/<docsPathnamePart>/<docsAppPathnamePart>/<docsPagePathnamePart>
 *
 * Pure function. Returns a new string.
 * Does not modify any parameters inline or perform any routing.
 *
 * @param locationPathname Value of location.pathname
 * @param pathnamePartsOverrides Values to override in the current docs route
 */
export const getFormattedDocsRoute = (
  locationPathname: string,
  pathnamePartsOverrides: PathnamePartsOverrides,
): string => {
  const locationPathnameSplit = locationPathname.split("/");

  const locationPathnameParts = {
    // /my-components/federated-modules-docs/my-components/mycomponentv1
    // /<appPathnamePart>/<docsPathnamePart>/<docsAppPathnamePart>/<docsPagePathnamePart>

    appPathnamePart: locationPathnameSplit[1],
    docsPathnamePart: locationPathnameSplit[2],
    docsAppPathnamePart: locationPathnameSplit[3],
    docsPagePathnamePart: locationPathnameSplit[4],
  };

  let resultDocsAppPathnamePart: string | undefined | null =
    pathnamePartsOverrides.docsApp || locationPathnameParts.docsAppPathnamePart;
  if (pathnamePartsOverrides.docsApp === null) {
    resultDocsAppPathnamePart = null;
  }
  resultDocsAppPathnamePart = resultDocsAppPathnamePart?.replace("/", "");

  let resultDocsPagePathnamePart: string | undefined | null =
    pathnamePartsOverrides.docsPage ||
    locationPathnameParts.docsPagePathnamePart;
  if (pathnamePartsOverrides.docsPage === null) {
    resultDocsPagePathnamePart = null;
  }
  resultDocsPagePathnamePart = resultDocsPagePathnamePart?.replace("/", "");

  const result = [
    locationPathnameParts.appPathnamePart,
    DOCS_PATHNAME_PART,
    resultDocsAppPathnamePart,
    resultDocsPagePathnamePart,
  ]
    .filter((x) => !!x)
    .join("/");

  return `/${result}`;
};

// Define types for routing functions below.

type GetDocsRoute = (locationPathname: string) => string;
type GetDocsAppRoute = (
  locationPathname: string,
  overrides?: { docsApp?: string | undefined },
) => string;
type GetDocsPageRoute = (
  locationPathname: string,
  overrides?: { docsPage?: string | undefined },
) => string;
type GetDocsAppPageRoute = (
  locationPathname: string,
  overrides: { docsApp: string; docsPage: string },
) => string;

type UseGetDocsRoute = () => () => ReturnType<GetDocsRoute>;
type UseGetDocsAppRoute = () => (
  overrides?: Parameters<GetDocsAppRoute>[1],
) => ReturnType<GetDocsAppRoute>;
type UseGetDocsPageRoute = () => (
  overrides?: Parameters<GetDocsPageRoute>[1],
) => ReturnType<GetDocsPageRoute>;
type UseGetDocsAppPageRoute = () => (
  overrides: Parameters<GetDocsAppPageRoute>[1],
) => ReturnType<GetDocsAppPageRoute>;

/**
 * Get the current docs route, removing the docsApp and docsPage parts of the route.
 *
 * Pure function. Returns a new string.
 * Does not modify any parameters inline or perform any routing.
 *
 * @param locationPathname Value of location.pathname
 */
export const getDocsRoute: GetDocsRoute = (locationPathname) =>
  getFormattedDocsRoute(locationPathname, { docsApp: null, docsPage: null });

/**
 * Get the current docs route, setting the docsApp part of the route (if given)
 * and removing the docsPage part of the route.
 *
 * Pure function. Returns a new string.
 * Does not modify any parameters inline or perform any routing.
 *
 * @param locationPathname Value of location.pathname
 * @param overrides Values to override in the current docs route
 */
export const getDocsAppRoute: GetDocsAppRoute = (
  locationPathname,
  { docsApp } = {},
) => getFormattedDocsRoute(locationPathname, { docsApp, docsPage: null });

/**
 * Get the current docs route, setting the docsPage part of the route (if given).
 *
 * Pure function. Returns a new string.
 * Does not modify any parameters inline or perform any routing.
 *
 * @param locationPathname Value of location.pathname
 * @param overrides Values to override in the current docs route
 */
export const getDocsPageRoute: GetDocsPageRoute = (
  locationPathname,
  { docsPage } = {},
) => getFormattedDocsRoute(locationPathname, { docsPage });

/**
 * Get the current docs route, setting the docsApp and docsPage parts of the route.
 *
 * Pure function. Returns a new string.
 * Does not modify any parameters inline or perform any routing.
 *
 * @param locationPathname Value of location.pathname
 * @param overrides Values to override in the current docs route
 */
export const getDocsAppPageRoute: GetDocsAppPageRoute = (
  locationPathname,
  { docsApp, docsPage },
) => getFormattedDocsRoute(locationPathname, { docsApp, docsPage });

/**
 * Convenience hook wrapper around getDocsRoute.
 */
export const useGetDocsRoute: UseGetDocsRoute = () => {
  const pathname = useLocation().pathname;
  return useCallback(() => getDocsRoute(pathname), [pathname]);
};

/**
 * Convenience hook wrapper around getDocsAppRoute.
 */
export const useGetDocsAppRoute: UseGetDocsAppRoute = () => {
  const pathname = useLocation().pathname;
  return useCallback(
    (overrides) => getDocsAppRoute(pathname, overrides),
    [pathname],
  );
};

/**
 * Convenience hook wrapper around getDocsPageRoute.
 */
export const useGetDocsPageRoute: UseGetDocsPageRoute = () => {
  const pathname = useLocation().pathname;
  return useCallback(
    (overrides) => getDocsPageRoute(pathname, overrides),
    [pathname],
  );
};

/**
 * Convenience hook wrapper around getDocsAppPageRoute.
 */
export const useGetDocsAppPageRoute: UseGetDocsAppPageRoute = () => {
  const pathname = useLocation().pathname;
  return useCallback(
    (overrides) => getDocsAppPageRoute(pathname, overrides),
    [pathname],
  );
};
